import { useRolodexEntries } from 'features/rolodex'
import { EventData } from 'features/systemLens'
import { useMemo } from 'react'
import { GeckoWorkOrder, RolodexEntryType } from 'types'

/**
 * Custom hook to fetch and transform work orders into operational events
 * for display on the time series chart
 */
export function useOperationalEvents(unitID: string | undefined): {
  operationalEvents: EventData[]
  isLoading: boolean
} {
  // Fetch work orders for operational events
  const { entries: workOrderEntries, isLoading } = useRolodexEntries(
    unitID
      ? {
          tags: { unit: unitID },
          type: RolodexEntryType.ASSET_LENS_WORK_ORDER,
        }
      : undefined
  )

  // Transform work orders into operational events
  const operationalEvents: EventData[] = useMemo(() => {
    if (!workOrderEntries) return []

    return workOrderEntries
      .filter((entry) => {
        const workOrder = entry.data as GeckoWorkOrder['data']
        // Include completed, scheduled, and in-progress work orders
        return ['Completed', 'Scheduled', 'In Progress'].includes(
          workOrder.status
        )
      })
      .map((entry) => {
        const workOrder = entry.data as GeckoWorkOrder['data']

        // Use appropriate date based on status
        let eventDate = workOrder.createdDate
        if (workOrder.status === 'Scheduled' && workOrder.dueDate) {
          eventDate = workOrder.dueDate
        }

        const timestamp = new Date(eventDate).getTime()

        // Build description with status
        const statusIndicator = workOrder.status ? ` (${workOrder.status})` : ''
        const description = `${workOrder.title}${statusIndicator}`

        // Store metadata for rendering
        const metadata = {
          status: workOrder.status,
          priority: workOrder.priority,
          assignedTo: workOrder.assignedTo,
          anomalyDetected: workOrder.anomalyDetected,
        }

        return {
          timestamp,
          type: 'Work Order',
          description,
          metadata,
        }
      })
      .sort((a, b) => a.timestamp - b.timestamp) // Sort by date
  }, [workOrderEntries])

  return {
    operationalEvents,
    isLoading,
  }
}
